// src/pages/CitySelectionDesktop.jsx
import React, { useMemo, useState, useCallback } from "react";
import { Container, Row, Col, Card, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

// ---- helper: scoped id for CSS isolation ----
function useScopeId(prefix = "csd") {
  return useMemo(
    () => `${prefix}-${Math.random().toString(36).slice(2, 8)}`,
    [prefix]
  );
}

// ---- default data (can be overridden via <CitySelectionDesktop cities={...} />) ----
const DEFAULT_CITIES = [
  {
    id: "montreal",
    name: "Montreal",
    country: "Canada",
    desc: "European charm and vibrant arts scene with rich culinary culture.",
    image:
      "https://aebc975c.delivery.rocketcdn.me/wp-content/uploads/2021/12/marc-olivier-jodoin-BG9oZ15a4Xk-unsplash-1-scaled.jpg.webp",
    tags: ["Old Port", "Museums", "Bagels"],
    rating: 4.8,
    temp: 15,
    population: "1.8M",
    categories: ["Historical", "Food Paradise"],
  },
  {
    id: "toronto",
    name: "Toronto",
    country: "Canada",
    desc: "A multicultural metropolis with iconic skyline and diverse food.",
    image:
      "https://travel.destinationcanada.com/_next/image?url=https%3A%2F%2Fadmin.destinationcanada.com%2Fsites%2Fdefault%2Ffiles%2Fimages%2Farticle%2Fwhat_to_do_in_toronto_header.jpg&w=1920&q=75",
    tags: ["CN Tower", "Toronto Islands", "Kensington"],
    rating: 4.6,
    temp: 17,
    population: "2.9M",
    categories: ["Modern", "Food Paradise"],
  },
  {
    id: "vancouver",
    name: "Vancouver",
    country: "Canada",
    desc: "Where mountains meet the ocean—hikes, views and fresh air.",
    image:
      "https://images.unsplash.com/photo-1444090542259-0af8fa96557e?q=80&w=1974&auto=format&fit=crop",
    tags: ["Stanley Park", "Grouse", "Granville"],
    rating: 4.7,
    temp: 16,
    population: "675K",
    categories: ["Natural Beauty", "Modern"],
  },
  {
    id: "newyork",
    name: "New York",
    country: "USA",
    desc: "The city that never sleeps—culture, shows and world-class dining.",
    image:
      "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?q=80&w=1974&auto=format&fit=crop",
    tags: ["Broadway", "Central Park", "Times Sq"],
    rating: 4.7,
    temp: 18,
    population: "8.5M",
    categories: ["Modern", "Food Paradise"],
  },
  {
    id: "losangeles",
    name: "Los Angeles",
    country: "USA",
    desc: "Beach vibes, canyons, and Hollywood glamour all in one place.",
    image:
      "https://www.extraspace.com/blog/wp-content/uploads/2018/11/living-in-los-angeles.jpg.webp",
    tags: ["Santa Monica", "Griffith", "Hollywood"],
    rating: 4.5,
    temp: 22,
    population: "3.9M",
    categories: ["Natural Beauty", "Modern", "Food Paradise"],
  },
  {
    id: "london",
    name: "London",
    country: "UK",
    desc: "Classics meet modern trends—museums, markets and the Thames.",
    image:
      "https://images.unsplash.com/photo-1488747279002-c8523379faaa?q=80&w=1974&auto=format&fit=crop",
    tags: ["Westminster", "British Museum", "Thames"],
    rating: 4.8,
    temp: 14,
    population: "8.9M",
    categories: ["Historical", "Modern"],
  },
];

const CATEGORIES = [
  "All Cities",
  "Historical",
  "Modern",
  "Food Paradise",
  "Natural Beauty",
];

export default function CitySelectionDesktop({ cities: inputCities }) {
  const scope = useScopeId(); // e.g. csd-ab12xy
  const navigate = useNavigate();

  // state
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("All Cities");
  const [view, setView] = useState("grid"); // 'grid' | 'list'
  const [favs, setFavs] = useState(() => new Set());

  const cities = useMemo(() => inputCities || DEFAULT_CITIES, [inputCities]);
  const normalizedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    return cities.filter((c) => {
      const matchText =
        c.name.toLowerCase().includes(normalizedQuery) ||
        c.country.toLowerCase().includes(normalizedQuery) ||
        c.tags?.some((t) => t.toLowerCase().includes(normalizedQuery));
      const matchCat =
        activeCat === "All Cities" || c.categories?.includes(activeCat);
      return matchText && matchCat;
    });
  }, [cities, normalizedQuery, activeCat]);

  const toggleFav = useCallback((id) => {
    setFavs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const onExplore = (id) =>
    navigate("/features", { state: { selectedCity: id } });

  // ---- scoped CSS with unique attribute prefix ----
  const css = `
  [data-csd="${scope}"] .csd-hero { padding: 20px 0 8px; }
  [data-csd="${scope}"] .csd-title { font-weight: 800; font-size: 28px; color: #0f1720; margin: 0; }
  [data-csd="${scope}"] .csd-sub { color:#6a7a8a; margin-top:4px }

  [data-csd="${scope}"] .csd-toolbar { display:flex; gap:12px; align-items:center; margin: 16px 0 18px; flex-wrap:wrap; }
  [data-csd="${scope}"] .csd-search { max-width: 420px; flex: 1 1 280px; }
  [data-csd="${scope}"] .chip { padding:8px 12px; border-radius:999px; border:1px solid #e7edf5; background:#f6f8fb;
                                 font-weight:600; font-size:13px; color:#0f1720; cursor:pointer; }
  [data-csd="${scope}"] .chip.active { background:#0f1720; color:#fff; border-color:#0f1720; }
  [data-csd="${scope}"] .csd-viewbtn { width:38px; height:38px; border-radius:10px; border:1px solid #e7edf5; background:#fff; display:inline-flex; align-items:center; justify-content:center; }

  [data-csd="${scope}"] .csd-card { border:1px solid #e8edf2; border-radius:18px; overflow:hidden; background:#fff; position:relative; }
  [data-csd="${scope}"] .csd-fav { position:absolute; top:12px; right:12px; width:36px; height:36px; border-radius:12px; background:#fff; border:1px solid #e7edf2; display:flex; align-items:center; justify-content:center; }
  [data-csd="${scope}"] .csd-fav.active { color:#e03131; border-color:#ffd9d9; background:#fff5f5; }

  [data-csd="${scope}"] .csd-img { height:180px; object-fit:cover; width:100%; background:#f4f6f9; }
  [data-csd="${scope}"] .csd-body { padding:16px 18px; }
  [data-csd="${scope}"] .csd-name { font-size:18px; font-weight:700; color:#0f1720; margin:8px 0 4px; }
  [data-csd="${scope}"] .csd-desc { color:#5b6b7a; min-height:48px; }
  [data-csd="${scope}"] .csd-meta { display:flex; gap:18px; color:#6a7a8a; font-size:13px; margin: 8px 0 10px; }
  [data-csd="${scope}"] .csd-tags { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:12px; }
  [data-csd="${scope}"] .csd-tag { background:#eef2f7; border-radius:999px; padding:6px 10px; font-size:12px; color:#334354; }
  [data-csd="${scope}"] .csd-cta { background:#1b2a41; border:none; border-radius:10px; padding:10px 12px; width:100%; }

    /* === Topbar === */
  [data-csd="${scope}"] .csd-headline{
    display:flex; align-items:center; gap:12px; padding:8px 0 6px;
  }
  [data-csd="${scope}"] .csd-back{
    display:inline-flex; align-items:center; gap:6px;
    border:none; background:transparent; padding:0; margin-right:4px;
    color:#0f1720; font-weight:600; cursor:pointer;
  }
  [data-csd="${scope}"] .csd-back:hover{ opacity:.8; }
  [data-csd="${scope}"] .csd-appicon{
    width:36px; height:36px; border-radius:12px; flex:0 0 36px;
    background:#0f2538; color:#fff; display:flex; align-items:center; justify-content:center;
    box-shadow:0 6px 18px rgba(0,0,0,.08);
    font-size:18px;
  }
  [data-csd="${scope}"] .csd-titlewrap h2{
    font-weight:800; font-size:22px; line-height:1.15; color:#0f1720; margin:0;
  }
  [data-csd="${scope}"] .csd-titlewrap .csd-sub{
    margin-top:2px; color:#6a7a8a; font-size:14px;
  }

  /* list view */
  [data-csd="${scope}"] .csd-card.list { display:flex; gap:0; }
  [data-csd="${scope}"] .csd-card.list .csd-img { width:260px; height:auto; min-height:180px; flex-shrink:0; }
  [data-csd="${scope}"] .csd-card.list .csd-body { width:100%; }
  @media (max-width: 1200px) {
    [data-csd="${scope}"] .csd-card.list .csd-img { width:220px; }
  }
  `;

  return (
    <>
      <style>{css}</style>

      <Container data-csd={scope} className="csd-hero">
        <div className="csd-headline">
          <button className="csd-back" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left" /> Back to Welcome
          </button>

          <div className="csd-appicon">
            <img
              src="/assets/logocity.jpg"
              alt="CityGetAway"
              style={{ width: 40, height: 40 }}
            />
          </div>

          <div className="csd-titlewrap">
            <h2>Choose Your Destination</h2>
            <div className="csd-sub">Select a city to start your adventure</div>
          </div>
        </div>

        {/* toolbar */}
        <div className="csd-toolbar">
          <Form.Control
            className="csd-search"
            size="lg"
            placeholder="Search cities..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={`chip ${activeCat === c ? "active" : ""}`}
              onClick={() => setActiveCat(c)}
            >
              {c}
            </button>
          ))}

          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button
              className="csd-viewbtn"
              title="Grid view"
              onClick={() => setView("grid")}
            >
              <i className="bi bi-grid-3x3-gap" />
            </button>
            <button
              className="csd-viewbtn"
              title="List view"
              onClick={() => setView("list")}
            >
              <i className="bi bi-list" />
            </button>
            <button className="csd-viewbtn" title="More filters">
              <i className="bi bi-funnel" />
            </button>
          </div>
        </div>

        {/* grid/list */}
        <Row className="g-3">
          {filtered.map((c) => (
            <Col
              key={c.id}
              lg={view === "grid" ? 4 : 12}
              md={view === "grid" ? 6 : 12}
            >
              <Card
                className={`csd-card h-100 ${view === "list" ? "list" : ""}`}
              >
                {/* fav */}
                <button
                  aria-label="Save city"
                  className={`csd-fav ${favs.has(c.id) ? "active" : ""}`}
                  onClick={() => toggleFav(c.id)}
                  title={
                    favs.has(c.id)
                      ? "Remove from favorites"
                      : "Add to favorites"
                  }
                >
                  <i
                    className={`bi ${
                      favs.has(c.id) ? "bi-heart-fill" : "bi-heart"
                    }`}
                  />
                </button>

                <img
                  className="csd-img"
                  src={c.image}
                  alt={c.name}
                  loading="lazy"
                />
                <Card.Body className="csd-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="csd-name">
                      {c.name}{" "}
                      <span
                        className="text-muted"
                        style={{ fontWeight: 500, fontSize: 14 }}
                      >
                        • {c.country}
                      </span>
                    </div>
                    <div className="text-warning">
                      <i className="bi bi-star-fill" />{" "}
                      {Number(c.rating).toFixed(1)}
                    </div>
                  </div>

                  <div className="csd-desc">{c.desc}</div>

                  <div className="csd-meta">
                    <span>
                      <i className="bi bi-people" /> {c.population}
                    </span>
                    <span>
                      <i className="bi bi-thermometer" /> {c.temp}°C
                    </span>
                  </div>

                  <div className="csd-tags">
                    {c.tags?.map((t) => (
                      <span key={t} className="csd-tag">
                        {t}
                      </span>
                    ))}
                  </div>

                  <Button className="csd-cta" onClick={() => onExplore(c.id)}>
                    Explore {c.name}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* helper */}
        <Card
          className="mt-4"
          style={{ borderRadius: 18, border: "1px solid #e8edf2" }}
        >
          <Card.Body className="text-center">
            <div className="text-muted mb-2">
              <i className="bi bi-info-circle" />
            </div>
            <h5 className="mb-2">Need help choosing?</h5>
            <div className="text-muted">
              Each city offers unique experiences. Consider your interests:
              history, food, nature, or modern attractions.
            </div>
            <Button variant="outline-dark" className="mt-3">
              <i className="bi bi-question-circle me-1" /> Take City Quiz
            </Button>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
}
